<script setup lang="ts">
import { computed } from 'vue';
import {
    BCard, BSkeletonLoader, BIcon,
} from '@/design-system';
import { useTranslation } from '@/composables';
import type { TopProductType } from '@/types/dashboard';

type PropsType = {
    items: TopProductType[];
    isLoading?: boolean;
    maxItems?: number;
    title?: string;
    emptyText?: string;
};

const props = withDefaults(defineProps<PropsType>(), {
    isLoading: false,
    maxItems: 5,
    emptyText: undefined,
});

const { t } = useTranslation();

const displayedItems = computed(() => props.items.slice(0, props.maxItems));

const skeletonItems = computed(() => Array.from({ length: 4 }, (_, i) => i));

const isEmpty = computed(() => !props.isLoading && props.items.length === 0);
</script>

<template>
    <BCard
        variant="flat"
        class="b-top-products-list pa-0"
    >
        <template
            v-if="title"
            #title
        >
            <div class="text-body-2 font-weight-bold pa-4 pb-0">
                {{ title }}
            </div>
        </template>

        <div class="b-top-products-list__content pa-4">
            <BSkeletonLoader
                v-if="isLoading"
                type="list-item"
                height="48"
            >
                <div class="d-flex flex-column">
                    <div
                        v-for="index in skeletonItems"
                        :key="index"
                        class="b-top-products-list__skeleton-item"
                    />
                </div>
            </BSkeletonLoader>

            <div
                v-else-if="isEmpty"
                class="b-top-products-list__empty"
            >
                <BIcon
                    icon="mdi-package-variant-closed-remove"
                    size="32"
                    color="grey-400"
                />
                <span class="b-top-products-list__empty-text">
                    {{ emptyText ?? t('common.noDataAvailable') }}
                </span>
            </div>

            <div
                v-else
                class="d-flex flex-column"
            >
                <div
                    v-for="(item, index) in displayedItems"
                    :key="item.id"
                    class="b-top-products-list__item dashboard-animate-slide-in"
                    :class="`dashboard-stagger-${index + 1}`"
                >
                    <div class="b-top-products-list__rank">
                        {{ item.rank }}
                    </div>

                    <div class="b-top-products-list__info">
                        <div class="b-top-products-list__name">
                            {{ item.name }}
                        </div>
                        <div
                            v-if="item.subtitle"
                            class="b-top-products-list__subtitle"
                        >
                            {{ item.subtitle }}
                        </div>
                    </div>

                    <div class="b-top-products-list__value">
                        {{ item.formattedValue }}
                    </div>
                </div>
            </div>
        </div>
    </BCard>
</template>

<style scoped lang="scss">
@use '@/design-system/styles/dashboard-animations';

.b-top-products-list {
    border-radius: 8px;
    border: 1px solid rgb(204, 225, 235);
    transition: box-shadow 0.2s ease;
}

.b-top-products-list:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.b-top-products-list__content {
    min-height: 200px;
}

.b-top-products-list__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 200px;
    gap: 12px;
}

.b-top-products-list__empty-text {
    font-size: 13px;
    color: rgba(var(--v-theme-on-surface), 0.5);
}

.b-top-products-list__skeleton-item {
    height: 48px;
    border-radius: 8px;
    background-color: rgb(var(--v-theme-grey-50));
    opacity: 0.1;
    margin-bottom: 8px;

    &:last-child {
        margin-bottom: 0;
    }
}

.b-top-products-list__item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    background-color: rgb(var(--v-theme-grey-50));
    border-radius: 8px;
    margin-bottom: 8px;
    opacity: 0;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover {
        background-color: rgb(var(--v-theme-grey-100));
    }

    &:last-child {
        margin-bottom: 0;
    }
}

.b-top-products-list__rank {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    flex-shrink: 0;
    border-radius: 4px;
    background-color: rgb(var(--v-theme-accent-mint-light));
    color: rgb(var(--v-theme-accent-mint-dark));
    font-size: 11px;
    font-weight: 600;
}

.b-top-products-list__info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.b-top-products-list__name {
    font-size: 13px;
    font-weight: 500;
    color: rgb(var(--v-theme-on-surface));
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.b-top-products-list__subtitle {
    font-size: 11px;
    color: rgba(var(--v-theme-on-surface), 0.5);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.b-top-products-list__value {
    font-size: 13px;
    font-weight: 600;
    color: rgb(var(--v-theme-accent-mint-dark));
    flex-shrink: 0;
}
</style>
